import React, { useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { MaterialReactTable } from 'material-react-table';

// Renderer for FINANCE-CONTRACT-BALANCE-DATTO (and any other audit whose
// jobResults follow the same { summary, serviceLines } shape).
//
// Designed to look like the legacy Finance > Datto Balance panel:
//   - Three summary cards (Total Lines / Mismatched / Balanced).
//   - One MaterialReactTable with the same columns the report uses
//     (Code, Customer, Contract, Service, Contract, Actual, Variance).
//   - "Data as of" chip in the top-right showing the latest record update.
//   - Export-to-Excel button as a top-toolbar action.
//
// The data flows: the shell hands us cma.audit records filtered by run +
// account. Each record represents one customer; jobResults.serviceLines is
// that customer's per-service-line detail. We flatten across customers so
// the table reads exactly like the old Finance report.
function ServiceLines({ records, inFlight, onRequeue }) {
  const theme = useTheme();

  // Flatten records -> one row per service line, prepending the customer
  // code and name. Mismatched lines bubble to the top. Each row keeps a
  // reference back to its source cma.audit record so the per-row Rerun
  // action can pass the right doc to onRequeue.
  const rows = useMemo(() => {
    const out = [];
    for (const r of records || []) {
      const jr = r.jobResults || {};
      const lines = jr.serviceLines || [];
      const customerName = jr.accountName || r.account || '';
      for (const line of lines) {
        out.push({
          customer_code:   r.account,
          customer_name:   customerName,
          contract_name:   line.contractName,
          service_type:    line.deviceType,
          contract_units:  line.contractUnits,
          actual_devices:  line.actualDevices,
          variance:        line.variance,
          _source:         r,   // back-reference for row actions
        });
      }
    }
    out.sort((a, b) => {
      // Mismatches first, then by customer code.
      const am = a.variance !== 0 ? 0 : 1;
      const bm = b.variance !== 0 ? 0 : 1;
      if (am !== bm) return am - bm;
      return (a.customer_code || '').localeCompare(b.customer_code || '');
    });
    return out;
  }, [records]);

  // Roll-up across all visible records.
  const summary = useMemo(() => {
    let total = 0, balanced = 0, mismatched = 0;
    for (const r of records || []) {
      const s = (r.jobResults && r.jobResults.summary) || {};
      total      += s.totalServiceLines || 0;
      balanced   += s.balancedLines     || 0;
      mismatched += s.mismatchedLines   || 0;
    }
    return { total, balanced, mismatched };
  }, [records]);

  // Most recent lastUpdated across visible records, formatted for the chip.
  const dataAsOf = useMemo(() => {
    let latest = '';
    for (const r of records || []) {
      const t = r.lastUpdated || '';
      if (t > latest) latest = t;
    }
    if (!latest) return '';
    const d = new Date(latest);
    if (Number.isNaN(d.getTime())) return latest;
    return d.toLocaleString('en-US', {
      month:  'short',
      day:    'numeric',
      year:   'numeric',
      hour:   'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }, [records]);

  const columns = useMemo(() => ([
    { accessorKey: 'customer_code',  header: 'Code',     size: 100 },
    { accessorKey: 'customer_name',  header: 'Customer', size: 200 },
    { accessorKey: 'contract_name',  header: 'Contract', size: 180 },
    { accessorKey: 'service_type',   header: 'Service',  size: 120 },
    { accessorKey: 'contract_units', header: 'Contract', size: 100 },
    { accessorKey: 'actual_devices', header: 'Actual',   size: 100 },
    {
      accessorKey: 'variance',
      header: 'Variance',
      size: 100,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          size="small"
          color={cell.getValue() < 0 ? 'error' : cell.getValue() > 0 ? 'warning' : 'success'}
        />
      ),
    },
  ]), []);

  if (!records || records.length === 0) {
    return (
      <Typography color="text.secondary">
        No Datto balance data for the current filters.
      </Typography>
    );
  }

  return (
    <>
      {dataAsOf && (
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            label={`Data as of: ${dataAsOf}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1, background: alpha(theme.palette.info.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{summary.total}</Typography>
            <Typography variant="caption" color="text.secondary">Total Lines</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.error.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{summary.mismatched}</Typography>
            <Typography variant="caption" color="text.secondary">Mismatched</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, background: alpha(theme.palette.success.main, 0.1) }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>{summary.balanced}</Typography>
            <Typography variant="caption" color="text.secondary">Balanced</Typography>
          </CardContent>
        </Card>
      </Stack>

      <MaterialReactTable
        key={`datto-${rows.length}`}
        columns={columns}
        data={rows}
        enableDensityToggle={false}
        enableRowActions={!!onRequeue}
        positionActionsColumn="last"
        displayColumnDefOptions={{
          'mrt-row-actions': { header: '', size: 64 },
        }}
        muiTableBodyRowProps={{ hover: true }}
        renderRowActions={({ row }) => {
          const record = row.original._source;
          const flight = inFlight && record ? inFlight[record.id] : null;

          // In-flight: replace the Rerun icon with a spinner + the queue
          // status text. Always visible so the operator can see what's
          // moving regardless of hover.
          if (flight) {
            return (
              <Tooltip title={`Job ${flight.status} for ${row.original.customer_code}`}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CircularProgress size={16} thickness={5} />
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform: 'capitalize' }}>
                    {flight.status}
                  </Typography>
                </Stack>
              </Tooltip>
            );
          }

          // Idle: hover-only Rerun. The opacity-0 default keeps the row
          // visually quiet; '.MuiTableRow-root:hover &' brings the button
          // in when the operator hovers the row.
          return (
            <Tooltip title={`Re-queue audit for ${row.original.customer_code}`}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRequeue && onRequeue(record);
                }}
                sx={{
                  opacity: 0,
                  transition: 'opacity 0.15s',
                  '.MuiTableRow-root:hover &': { opacity: 1 },
                  '&:focus-visible': { opacity: 1 },
                }}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }}
        renderTopToolbarCustomActions={() => (
          <Button
            onClick={() => {
              const csv = rows.map(row =>
                columns.map(col => {
                  const value = row[col.accessorKey];
                  const s = value?.toString() || '';
                  return s.includes(',') || s.includes('"')
                    ? `"${s.replace(/"/g, '""')}"`
                    : s;
                }).join(',')
              );
              const header = columns.map(col => col.header).join(',');
              const csvContent = [header, ...csv].join('\n');
              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `datto-balance-${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
            }}
            variant="contained"
            size="small"
            sx={{ ml: 2 }}
          >
            Export to Excel
          </Button>
        )}
        initialState={{ density: 'compact', pagination: { pageIndex: 0, pageSize: 25 } }}
        muiTableContainerProps={{ sx: { maxHeight: '60vh' } }}
      />
    </>
  );
}

export default ServiceLines;
